import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trophy, ChevronRight, MapPin, Ticket, Disc3, CheckCircle2 } from "lucide-react";
import api, { thumbUrl } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtFull } from "@/lib/format";

export default function Tours() {
  const navigate = useNavigate();
  const [tours, setTours] = useState(null);

  useEffect(() => {
    api.get("/tours")
      .then((r) => setTours(r.data.tours || []))
      .catch(() => { toast.error("Failed to load tours"); setTours([]); });
  }, []);

  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[#35606E] text-white">
            <Trophy className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">Tour Leaderboards</h1>
            <p className="text-sm text-neutral-500">Artists on tour with live ticket-count leaderboards</p>
          </div>
        </div>

        {tours === null ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            <Skeleton className="h-36 rounded-2xl" /><Skeleton className="h-36 rounded-2xl" /><Skeleton className="h-36 rounded-2xl" />
          </div>
        ) : tours.length === 0 ? (
          <div data-testid="tours-empty" className="pb-card flex flex-col items-center gap-3 p-16 text-center">
            <Trophy className="h-10 w-10 text-neutral-300" />
            <p className="font-display text-xl font-bold">No tours yet</p>
            <p className="text-sm text-neutral-500">No artist currently has a tour leaderboard.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {tours.map((t, i) => (
              <motion.button
                key={t.slug}
                data-testid={`tour-card-${t.slug}`}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                onClick={() => navigate(`/tour/${t.slug}`)}
                className="pb-card group flex items-center gap-4 p-5 text-left transition-transform hover:-translate-y-1"
              >
                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-2xl bg-neutral-900 ring-1 ring-black/5">
                  {t.image ? (
                    <img src={thumbUrl(t.image, 160)} alt={t.artist} width={80} height={80} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  ) : (
                    <div className="grid h-full place-items-center text-neutral-600"><Disc3 className="h-7 w-7" /></div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#6E97A2]">{t.tour}</p>
                  <h2 className="truncate font-display text-xl font-bold leading-tight">{t.artist}</h2>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
                    {t.tickets_sold != null && (
                      <span className="flex items-center gap-1"><Ticket className="h-3.5 w-3.5" /> {fmtFull(t.tickets_sold)} sold</span>
                    )}
                    {t.cities != null && (
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {t.cities} cities</span>
                    )}
                    {t.sold_out ? (
                      <span className="flex items-center gap-1 text-[#065F46]"><CheckCircle2 className="h-3.5 w-3.5" /> {t.sold_out} sold out</span>
                    ) : null}
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-neutral-300 transition-transform group-hover:translate-x-1 group-hover:text-neutral-900" />
              </motion.button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
